/* ------------------------------------------------------------------
   AnalyticsPage.js — Social Media › Analytics (Part E, T29).
   Laid out like .jarvis/design/features/Main.dc.html: weekly-report
   checkbox, accounts filter + 7 / 28 / 90 days, four numbers with the
   change vs the previous period, follower growth (chart or table),
   By network, Top posts with 'Post again'.
   Data: GET /api/analytics/summary | followers | networks | top-posts,
   GET/PUT /api/analytics/report-settings (docs/MOBILE_API.md › Analytics).
   Free / Starter get 403 upgrade_required → a friendly upgrade card.
------------------------------------------------------------------ */
import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import FollowerChart from './FollowerChart'

const ax = axios.create({ withCredentials: true })
const RANGES = [7, 28, 90]

const card = { background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12 }
const muted = '#6b7280'
const green = '#15803d'
const red = '#b91c1c'

/* ── formatting ── */
export function compact (n) {
  if (n === null || n === undefined) return '—'
  const abs = Math.abs(n)
  if (abs >= 1e6) return `${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M`
  if (abs >= 1e4) return `${(n / 1e3).toFixed(1).replace(/\.0$/, '')}K`
  return Math.round(n).toLocaleString('en-US')
}
export const percent = v => (v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}%`)
const arrow = n => (n >= 0 ? '▲' : '▼')

function Stat ({ label, value, note, tone }) {
  return (
    <div style={{ ...card, padding: '14px 16px' }} data-testid={`stat-${label}`}>
      <div style={{ fontSize: 11, color: muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, marginTop: 4 }}>{value}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: tone === 'up' ? green : tone === 'down' ? red : muted }}>{note}</div>
    </div>
  )
}

function UpgradeCard ({ message }) {
  return (
    <div style={{ ...card, padding: 28, maxWidth: 640 }} data-testid='analytics-upgrade'>
      <div style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, background: '#eff6ff', color: '#2563eb', padding: '3px 8px', borderRadius: 6, marginBottom: 12 }}>PRO FEATURE</div>
      <h3 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 8px' }}>See what's working with Analytics & Insights</h3>
      <p style={{ color: '#374151', fontSize: 14, margin: '0 0 6px' }}>
        {message || 'Analytics & Insights is part of the Pro and Agency plans.'}
      </p>
      <ul style={{ color: '#374151', fontSize: 14, paddingLeft: 18, margin: '0 0 18px' }}>
        <li>Followers, reach and engagement rate for every connected account</li>
        <li>Follower growth over 7, 28 or 90 days</li>
        <li>Your top posts, ready to post again</li>
        <li>A weekly report in your inbox</li>
      </ul>
      <Link to='/dashboard/plans' style={{ display: 'inline-block', padding: '10px 18px', background: '#2563eb', color: '#fff', borderRadius: 8, fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>
        See plans
      </Link>
    </div>
  )
}

export default function AnalyticsPage () {
  const navigate = useNavigate()
  const [range, setRange] = useState(28)
  const [accountKey, setAccountKey] = useState('')
  const [accounts, setAccounts] = useState([])
  const [data, setData] = useState(null)        // { summary, followers, networks, top }
  const [state, setState] = useState('loading') // loading | ready | upgrade | error
  const [upgradeMsg, setUpgradeMsg] = useState(null)
  const [allPosts, setAllPosts] = useState(false)
  const [report, setReport] = useState({ weeklyReport: false, available: false })
  const [reportMsg, setReportMsg] = useState(null)
  const [copying, setCopying] = useState(null)

  const load = useCallback(async () => {
    setState(s => (s === 'ready' ? 'ready' : 'loading'))
    const params = { range, accounts: accountKey || undefined }
    try {
      const [summary, followers, networks, top] = await Promise.all([
        ax.get('/api/analytics/summary', { params }),
        ax.get('/api/analytics/followers', { params }),
        ax.get('/api/analytics/networks', { params }),
        ax.get('/api/analytics/top-posts', { params: { ...params, limit: allPosts ? 20 : 5 } }),
      ])
      if (!accountKey) setAccounts(summary.data.accounts || [])
      setData({ summary: summary.data, followers: followers.data, networks: networks.data, top: top.data })
      setState('ready')
    } catch (e) {
      if (e.response?.status === 403 && e.response.data?.error === 'upgrade_required') {
        setUpgradeMsg(e.response.data.message)
        setState('upgrade')
      } else {
        setState('error')
      }
    }
  }, [range, accountKey, allPosts])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    ax.get('/api/analytics/report-settings')
      .then(({ data: d }) => setReport({ weeklyReport: !!d.weeklyReport, available: !!d.available }))
      .catch(() => {})
  }, [])

  const toggleReport = async e => {
    const on = e.target.checked
    setReport(r => ({ ...r, weeklyReport: on }))
    setReportMsg(null)
    try {
      const { data: d } = await ax.put('/api/analytics/report-settings', { weeklyReport: on })
      setReport({ weeklyReport: !!d.weeklyReport, available: true })
      setReportMsg(on ? 'We’ll email you every Monday morning.' : 'Weekly report turned off.')
    } catch (err) {
      setReport(r => ({ ...r, weeklyReport: !on }))
      setReportMsg(err.response?.data?.message || 'Could not save that. Please try again.')
    }
  }

  const postAgain = async post => {
    setCopying(post.id)
    try {
      const { data: d } = await ax.post(`/api/analytics/top-posts/${post.id}/post-again`)
      navigate('/dashboard/post', { state: { draft: d.draft } })
    } catch {
      setCopying(null)
    }
  }

  const header = (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <div>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', margin: '0 0 4px 0' }}>Analytics</h2>
        <p style={{ fontSize: 14, color: muted, margin: 0 }}>How your posts and accounts are doing.</p>
      </div>
      {state !== 'upgrade' && (
        <div style={{ textAlign: 'right' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#374151', cursor: report.available ? 'pointer' : 'default' }}>
            <input type='checkbox' checked={report.weeklyReport} disabled={!report.available} onChange={toggleReport} />
            Email me a weekly report
          </label>
          {(reportMsg || !report.available) && (
            <div role='status' style={{ fontSize: 12, color: muted, marginTop: 2 }}>{reportMsg || 'Weekly reports are coming soon.'}</div>
          )}
        </div>
      )}
    </div>
  )

  if (state === 'upgrade') {
    return <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>{header}<UpgradeCard message={upgradeMsg} /></div>
  }

  const s = data?.summary
  const needsAttention = accounts.filter(a => a.statsStatus !== 'ok')
  const scope = accountKey ? (accounts.find(a => a.key === accountKey)?.name || 'This account') : 'All accounts'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} data-testid='analytics-page'>
      <style>{`
        .an-stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
        .an-mid   { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 12px; }
        .an-top   { display: grid; grid-template-columns: 52px minmax(0, 1fr) 100px 80px 80px 80px 90px; gap: 10px 14px; align-items: center; font-size: 13px; }
        @media (max-width: 991px) {
          .an-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .an-mid   { grid-template-columns: minmax(0, 1fr); }
          .an-mid > * { grid-column: auto !important; }
          .an-top   { grid-template-columns: 52px minmax(0, 1fr) 70px 90px; }
          .an-top .an-wide { display: none; }
        }
      `}</style>

      {header}

      <div style={{ ...card, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '10px 14px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', fontWeight: 600 }}>
          Accounts
          <select
            value={accountKey}
            onChange={e => setAccountKey(e.target.value)}
            style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8, font: '500 13px inherit', background: '#fff' }}
          >
            <option value=''>All accounts ({accounts.length})</option>
            {accounts.map(a => <option key={a.key} value={a.key}>{a.networkLabel} · {a.name || a.accountId}</option>)}
          </select>
        </label>
        <div style={{ flex: 1 }} />
        <div role='group' aria-label='Time range' style={{ display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden' }}>
          {RANGES.map((r, i) => (
            <button
              key={r}
              type='button'
              aria-pressed={range === r}
              onClick={() => setRange(r)}
              style={{
                padding: '7px 14px', font: '600 13px inherit', border: 0, borderLeft: i ? '1px solid #d1d5db' : 0, cursor: 'pointer',
                background: range === r ? '#2563eb' : '#fff', color: range === r ? '#fff' : '#374151',
              }}
            >
              {r} days
            </button>
          ))}
        </div>
      </div>

      {state === 'loading' && <div style={{ color: muted, fontSize: 14 }}>Loading your stats…</div>}
      {state === 'error' && (
        <div role='alert' style={{ ...card, padding: 16, color: '#374151', fontSize: 14 }}>
          We couldn't load your stats just now. <button type='button' onClick={load} style={{ border: 0, background: 'none', color: '#2563eb', fontWeight: 600, padding: 0 }}>Try again</button>
        </div>
      )}

      {state === 'ready' && s && (
        <>
          {accounts.length === 0 && (
            <div style={{ ...card, padding: 16, fontSize: 14, color: '#374151' }}>
              Connect an account to see its stats here. <Link to='/dashboard/accounts' style={{ fontWeight: 600 }}>Connect an account</Link>
            </div>
          )}
          {accounts.length > 0 && !s.hasData && (
            <div style={{ ...card, padding: 16, fontSize: 14, color: '#374151' }}>
              No stats yet for {accountKey ? 'this account' : 'your accounts'}. We collect them every night, so your first numbers appear tomorrow.
            </div>
          )}
          {needsAttention.length > 0 && (
            <div style={{ ...card, padding: '12px 16px' }} data-testid='stats-status'>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Accounts without stats</div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {needsAttention.map(a => (
                  <li key={a.key} style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600 }}>{a.networkLabel} · {a.name || a.accountId}</span>
                    <span style={{ color: muted }}>{a.statsLabel}</span>
                    {a.statsStatus === 'needs_reconnect' && <Link to='/dashboard/accounts' style={{ fontWeight: 600 }}>Reconnect</Link>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className='an-stats'>
            <Stat
              label='Followers' value={compact(s.followers.total)}
              note={s.followers.change === null ? '—' : `${arrow(s.followers.change)} ${compact(Math.abs(s.followers.change))} this period`}
              tone={s.followers.change === null ? null : s.followers.change >= 0 ? 'up' : 'down'}
            />
            <Stat
              label='Reach' value={compact(s.reach.total)}
              note={s.reach.changePct === null ? 'no earlier period to compare' : `${arrow(s.reach.changePct)} ${Math.abs(s.reach.changePct)}% vs last period`}
              tone={s.reach.changePct === null ? null : s.reach.changePct >= 0 ? 'up' : 'down'}
            />
            <Stat label='Engagement rate' value={percent(s.engagementRate.value)} note='likes + comments ÷ reach' />
            <Stat label='Posts published' value={String(s.posts.published)} note={`${s.posts.failed} failed`} tone={s.posts.failed ? 'down' : null} />
          </div>

          <div className='an-mid'>
            <FollowerChart series={data.followers.series} rangeLabel={`${scope}, last ${range} days`} />
            <div style={{ ...card, gridColumn: 'span 2', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>By network</div>
              {data.networks.networks.length ? (
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'separate', borderSpacing: '0 6px' }}>
                  <caption className='visually-hidden'>Reach and engagement rate by network</caption>
                  <thead>
                    <tr>
                      <th scope='col' style={{ color: muted, fontWeight: 600, textAlign: 'left' }}>Network</th>
                      <th scope='col' style={{ color: muted, fontWeight: 600, textAlign: 'right' }}>Reach</th>
                      <th scope='col' style={{ color: muted, fontWeight: 600, textAlign: 'right' }}>Eng.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.networks.networks.map(n => (
                      <tr key={n.network}>
                        <td>
                          {n.label}
                          {!n.available && n.statsLabel && <div style={{ fontSize: 11.5, color: muted }}>{n.statsLabel}</div>}
                        </td>
                        <td style={{ textAlign: 'right' }}>{compact(n.reach)}</td>
                        <td style={{ textAlign: 'right' }}>{percent(n.engagementRate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <div style={{ fontSize: 13, color: muted }}>No connected accounts yet.</div>}
              <div style={{ marginTop: 'auto', fontSize: 12, color: muted }}>Networks that don't share stats with apps show "—".</div>
            </div>
          </div>

          <div style={{ ...card, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Top posts</div>
              {(data.top.posts.length >= 5 || allPosts) && (
                <button type='button' onClick={() => setAllPosts(v => !v)} style={{ border: 0, background: 'none', padding: 0, fontSize: 13, fontWeight: 600, color: '#2563eb' }}>
                  {allPosts ? 'Show top 5' : 'See all posts'}
                </button>
              )}
            </div>
            {data.top.posts.length ? (
              <div className='an-top' role='table' aria-label='Top posts'>
                <div role='row' style={{ display: 'contents' }}>
                  <span role='columnheader'><span className='visually-hidden'>Picture</span></span>
                  <span role='columnheader' style={{ color: muted, fontWeight: 600 }}>Post</span>
                  <span role='columnheader' style={{ color: muted, fontWeight: 600 }}>Network</span>
                  <span role='columnheader' className='an-wide' style={{ color: muted, fontWeight: 600, textAlign: 'right' }}>Reach</span>
                  <span role='columnheader' className='an-wide' style={{ color: muted, fontWeight: 600, textAlign: 'right' }}>Likes</span>
                  <span role='columnheader' className='an-wide' style={{ color: muted, fontWeight: 600, textAlign: 'right' }}>Comments</span>
                  <span role='columnheader'><span className='visually-hidden'>Action</span></span>
                </div>
                {data.top.posts.map(p => (
                  <div role='row' key={p.id} style={{ display: 'contents' }}>
                    <span role='cell'>
                      {p.mediaUrl
                        ? <img src={p.mediaUrl} alt='' style={{ width: 44, height: 55, objectFit: 'cover', borderRadius: 6, display: 'block', background: '#e5e7eb' }} />
                        : <div style={{ width: 44, height: 55, borderRadius: 6, background: 'linear-gradient(160deg, #dbeafe, #93c5fd)' }} />}
                    </span>
                    <span role='cell' title={p.caption} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {p.permalink ? <a href={p.permalink} target='_blank' rel='noreferrer' style={{ color: '#111827', textDecoration: 'none' }}>{p.caption || '(no caption)'}</a> : (p.caption || '(no caption)')}
                    </span>
                    <span role='cell'>{p.networkLabel}</span>
                    <span role='cell' className='an-wide' style={{ textAlign: 'right' }}>{compact(p.reach)}</span>
                    <span role='cell' className='an-wide' style={{ textAlign: 'right' }}>{compact(p.likes)}</span>
                    <span role='cell' className='an-wide' style={{ textAlign: 'right' }}>{compact(p.comments)}</span>
                    <span role='cell'>
                      <button
                        type='button'
                        disabled={copying === p.id}
                        onClick={() => postAgain(p)}
                        aria-label={`Post again: ${(p.caption || '').slice(0, 40)}`}
                        style={{ padding: '6px 10px', border: '1px solid #dbeafe', background: '#eff6ff', color: '#2563eb', borderRadius: 8, font: '600 12px inherit', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        Post again
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            ) : <div style={{ fontSize: 13, color: muted }}>No posts in this period yet.</div>}
          </div>
        </>
      )}
    </div>
  )
}
